import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Home, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [resetLink, setResetLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      if (res.data.reset_link) {
        setResetLink(res.data.reset_link);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center space-x-2 mb-8" data-testid="logo-link">
          <div className="w-12 h-12 bg-[#D97757] rounded-full flex items-center justify-center">
            <Home className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading font-semibold text-2xl text-[#2C3329]">NeighborBorrow</span>
        </Link>

        <div className="bg-white rounded-2xl border border-[#EBE8DF] p-8">
          {submitted ? (
            <div className="text-center" data-testid="forgot-password-success">
              <div className="w-16 h-16 bg-[#4CAF50]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#4CAF50]" />
              </div>
              {resetLink ? (
                <>
                  <h1 className="font-heading text-2xl font-semibold text-[#2C3329] mb-2">
                    Reset your password
                  </h1>
                  <p className="text-[#596652] mb-5 text-sm">
                    Click the button below to set a new password for your account. This link expires in 1 hour.
                  </p>
                  <a
                    href={resetLink}
                    className="inline-block bg-[#D97757] text-white rounded-full px-8 py-3 font-medium hover:bg-[#C56547] transition-colors mb-5"
                    data-testid="reset-password-direct-link"
                  >
                    Reset Password
                  </a>
                </>
              ) : (
                <>
                  <h1 className="font-heading text-2xl font-semibold text-[#2C3329] mb-2">
                    Check your email
                  </h1>
                  <p className="text-[#596652] mb-6">
                    If an account with <strong>{email}</strong> exists, we've sent a password reset link. Check your inbox (and spam folder).
                  </p>
                </>
              )}
              <Link
                to="/login"
                className="inline-flex items-center text-[#596652] hover:text-[#D97757] text-sm"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to login
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-[#D97757]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-[#D97757]" />
                </div>
                <h1 className="font-heading text-2xl font-semibold text-[#2C3329] mb-2">
                  Forgot your password?
                </h1>
                <p className="text-[#596652] text-sm">
                  Enter the email address associated with your account and we'll send you a link to reset your password.
                </p>
              </div>

              {error && (
                <div className="bg-[#E53935]/10 text-[#E53935] px-4 py-3 rounded-xl mb-4 text-sm" data-testid="forgot-password-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-[#2C3329]">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    required
                    className="mt-1 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                    placeholder="you@example.com"
                    data-testid="forgot-password-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D97757] text-white rounded-full hover:bg-[#C56547] py-3"
                  data-testid="forgot-password-submit-button"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
              </form>

              <div className="text-center mt-6">
                <Link
                  to="/login"
                  className="inline-flex items-center text-[#596652] hover:text-[#2C3329] text-sm"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
